import Config from '../config/config';

export type ChatModel = 'claude-sonnet-4-6' | 'claude-opus-4-6' | 'claude-sonnet-5' | 'claude-opus-5';

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
}

interface StreamEvent {
    type: string;
    data: Record<string, unknown>;
}

function parseSseChunk(chunk: string): StreamEvent[] {
    // The chatbot Lambda writes plain `event: <name>\ndata: <json>\n\n` frames
    // (see infra/cdk/lambdas/chatbot/src/handlers/chat.ts) - a minimal SSE
    // subset, not a general parser.
    return chunk
        .split('\n\n')
        .filter((block) => block.trim().length > 0)
        .map((block) => {
            const lines = block.split('\n');
            const eventLine = lines.find((l) => l.startsWith('event:'));
            const dataLine = lines.find((l) => l.startsWith('data:'));
            const type = eventLine ? eventLine.replace('event:', '').trim() : 'message';
            const raw = dataLine ? dataLine.replace('data:', '').trim() : '{}';
            let data: Record<string, unknown> = {};
            try {
                data = JSON.parse(raw);
            } catch {
                data = {};
            }
            return { type, data };
        });
}

/**
 * Streams a chat completion, invoking `onText` for each incremental chunk of
 * assistant text. Resolves with the final usage/cost info once the stream
 * ends (or rejects on a transport/auth error).
 */
export async function streamChat(
    token: string,
    messages: ChatMessage[],
    model: ChatModel,
    onText: (text: string) => void,
): Promise<{ costUsd?: number; model?: string }> {
    const url = Config.chat?.functionUrl;
    if (!url) {
        throw new Error('Chat API is not configured (missing config.chat.functionUrl)');
    }

    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ messages, model }),
    });

    if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let usage: { costUsd?: number; model?: string } = {};

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const boundary = buffer.lastIndexOf('\n\n');
        if (boundary === -1) continue;
        const complete = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        for (const event of parseSseChunk(complete)) {
            if (event.type === 'message' && typeof event.data.text === 'string') {
                onText(event.data.text);
            } else if (event.type === 'usage') {
                usage = { costUsd: event.data.costUsd as number | undefined, model: event.data.model as string | undefined };
            } else if (event.type === 'error') {
                throw new Error(String(event.data.error ?? 'Unknown chat error'));
            }
        }
    }

    return usage;
}
